/**
 * What a date filter host adds around Superset's picker so a keyboard and a screen reader can use
 * it. Measured on the committed bundle (blink-superset-bundle.md, "The x-filters hosts"):
 *
 *   - antd names the inputs by placeholder only, so they are named here from the host's label, and
 *     it rewrites their aria-invalid on its own renders, so the server's error is held on them;
 *   - the preset labels and the clear control are not focusable, so they are made buttons here;
 *   - Escape in an input closes the popup but still reaches window, where a surrounding
 *     <x-modal> would close too, so an Escape that closes the popup stops at the host;
 *   - after a click inside the popup focus is on <body>, where Escape does nothing, so the host
 *     closes its open popup from there and puts focus back on the input that opened it;
 *   - tabbing away leaves the popup open, so the host closes it when focus leaves;
 *   - Tab from the end input lands on the clear control, and antd closes the popup as the input
 *     blurs, so while it is open Tab goes from the last input straight to the first preset;
 *   - antd tells a pick in the popup from one typed in an input by a mousedown on the popup (its
 *     lastOperation 'panel'); without one, closing the popup runs its typed-input confirm on the
 *     active field, a second, partial change (the old start with the preset's end) that also drops
 *     the preset key. So a preset pressed from the keyboard is given the mousedown a click has —
 *     synthetic, so it moves no focus — and then the click;
 *   - the component records a preset's key in its label's click handler, while antd applies the
 *     preset from the whole row, so a click on the row's padding is passed to the label;
 *   - hovering a preset previews its dates in the inputs — the component's own browser-side,
 *     Sunday-week dates — so the host never lets that preview start: it shows PHP's dates only;
 *   - each calendar month is a Tab stop with no name that answers no key (cells are mouse-only; a
 *     keyboard types the date), so the months leave the Tab order, still focusable by a click;
 *   - the range separator and the calendar icon are exposed as images named "swap-right" and
 *     "calendar", which a screen reader reads between and after the dates, so they are hidden.
 *
 * Every preset press is reported to the host (onPresetPress) before antd sees it: the component
 * answers some presses with no DataMask at all, and the host must still treat them as picks.
 *
 * The class names are antd's (prefix "ant"), composed at runtime, so nothing can pin them against
 * the bundle. A changed prefix, or a popup with no preset list, is reported by a console.error below;
 * a renamed single class (the clear control, the hidden marker) is not, so re-check the layer in a
 * browser after any Superset upgrade that moves antd.
 */
const DROPDOWN = '.ant-picker-dropdown';
const HIDDEN = 'ant-picker-dropdown-hidden';
const PRESET_ROWS = '.ant-picker-presets li';
const PRESETS = '.ant-picker-presets';
// Any prefix: finds the popup when antd's `ant` prefix has been changed, so the change is reported.
const ANY_PREFIX_DROPDOWN = '[class*="-picker-dropdown"]';
const CLEAR = '.ant-picker-clear';
const PANELS = '.ant-picker-panel';
const DECORATIVE = '.ant-picker-separator, .ant-picker-suffix';
const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const activates = (event) => event.key === 'Enter' || event.key === ' ';
const tabbables = (scope) => Array.from(scope.querySelectorAll(TABBABLE))
    .filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);

export function createDateFilterKeyboard({
    root,
    mount,
    popup,
    kind,
    ids = {},
    presetOrder = () => [],
    text = {},
    popupContainer = null,
    required = false,
    currentPresetKey = () => null,
    onPresetPress = () => {},
}) {
    // The input the popup was opened from, by position (a fresh mount replaces the nodes). Recorded
    // at the opening key, press or click, not as the last input focused: Enter in the start input
    // opens the popup and moves focus to the end one, and focus returned there after a pick would
    // be antd's second visit to a field, after which the next typed start submits on its first Enter.
    let triggerIndex = 0;
    let escapeFoundOpen = false;
    // Where the reader last pressed: a range committed as they click away must not pull focus back.
    let pointerOutside = false;
    let serverInvalid = false;
    let warned = false;
    let warnedPrefix = false;

    const ancestor = namedAncestor();
    // The named ancestor gets a box of this host's own, so two hosts rendering into one ancestor
    // never read each other's popup. It carries the default box's classes: the focus ring lives there.
    const popupRoot = ancestor
        ? ancestor.appendChild(Object.assign(document.createElement('div'), { className: popup.className ?? '' }))
        : popup;

    /**
     * popup-container's ancestor, if it is one the popup can live in. Livewire's morph removes the
     * children its render does not have, so a box appended to an ancestor it patches is gone after
     * the next render and the popup would open into a detached node. The morph runs from the
     * component's root down, so an ancestor is safe outside every Livewire component, or under a
     * wire:ignore INSIDE the component (one above it stops nothing).
     */
    function namedAncestor() {
        if (! popupContainer) return null;

        let found;
        try {
            found = root.closest(popupContainer);
        } catch (error) {
            console.error('Date filter popup-container is not a valid selector, so the popup stays in the host', { id: ids.host, popupContainer });
            return null;
        }

        if (! found) {
            console.error('Date filter popup-container matches no ancestor, so the popup stays in the host', { id: ids.host, popupContainer });
            return null;
        }

        const component = found.closest('[wire\\:id]');
        const ignored = found.closest('[wire\\:ignore]');
        if (component && ! (ignored && component.contains(ignored))) {
            console.error('Date filter popup-container is morphed by Livewire, so the popup stays in the host', { id: ids.host });
            return null;
        }

        return found;
    }

    const inputs = () => Array.from(mount.querySelectorAll('input'));
    // A closing popup keeps its classes through a ~250ms leave animation (`ant-slide-up-leave`, or
    // slide-down when it opened upwards) before it is marked hidden; it is already closed, and an
    // Escape pressed meanwhile belongs to whatever is around the filter.
    const isOpen = () => {
        const dropdown = popupRoot.querySelector(DROPDOWN);
        return Boolean(dropdown)
            && ! dropdown.classList.contains(HIDDEN)
            && ! Array.from(dropdown.classList).some((name) => name.endsWith('-leave'));
    };
    const triggerInput = () => inputs()[triggerIndex] ?? inputs()[0] ?? null;
    const presetRows = () => Array.from(popupRoot.querySelectorAll(PRESET_ROWS));
    const labelOf = (row) => row.querySelector('span') ?? row;
    const presetButtons = () => Array.from(popupRoot.querySelectorAll('[data-date-filter-preset]'));

    /** antd closes on an Escape keydown at its input, focused or not (measured). */
    function close({ refocus }) {
        const input = triggerInput();
        if (! input) return;
        if (refocus) input.focus();
        input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true }));
    }

    /**
     * After a pick, focus is on <body>, on a preset the popup just hid, or on an input a fresh mount
     * removed; the reader's place is the input that opened the popup — unless they pressed outside
     * the filter, which is what committed a range typed without Enter.
     */
    function returnFocus() {
        const active = document.activeElement;
        const inPopup = active && popupRoot.contains(active);
        if (active && active !== document.body && ! inPopup) return;
        if (pointerOutside && ! inPopup) return;
        triggerInput()?.focus();
    }

    function makeButton(element, { name = null, prepare = null } = {}) {
        if (element.dataset.dateFilterButton === undefined) {
            element.dataset.dateFilterButton = '';
            element.setAttribute('role', 'button');
            element.tabIndex = 0;
            element.addEventListener('keydown', (event) => {
                if (! activates(event)) return;
                // Not antd's key: the picker around it would act on an Enter too.
                event.preventDefault();
                event.stopPropagation();
                prepare?.();
                element.click();
            });
        }
        if (name) element.setAttribute('aria-label', name);
    }

    function makeClearOperable() {
        mount.querySelectorAll(CLEAR).forEach((clear) => makeButton(clear, { name: text.clear }));
        mount.querySelectorAll(DECORATIVE).forEach((icon) => icon.setAttribute('aria-hidden', 'true'));
    }

    function makePresetsOperable() {
        if (! isOpen()) return;

        const rows = presetRows();
        const order = presetOrder();

        if (kind === 'dateRange' && order.length > 0 && rows.length === 0 && ! warned) {
            warned = true;
            console.error('Date filter popup has no preset list to make keyboard-operable', { id: ids.host });
        }

        popupRoot.querySelectorAll(PANELS).forEach((panel) => { panel.tabIndex = -1; });

        // The label span, not the row: its click handler is what records the preset key.
        rows.forEach((row, index) => {
            const label = labelOf(row);
            makeButton(label, { prepare: () => label.dispatchEvent(new MouseEvent('mousedown', { bubbles: true })) });
            label.dataset.dateFilterPreset = order[index] ?? '';
            label.setAttribute('aria-pressed', String(order[index] !== undefined && order[index] === currentPresetKey()));
        });
    }

    /** Names the inputs after every mount: React keeps the nodes, but a fresh mount makes new ones. */
    function wire({ invalid = false } = {}) {
        serverInvalid = invalid;
        const [start, end] = inputs();
        if (! start) return;

        start.id = ids.start;
        start.setAttribute('aria-labelledby', kind === 'date' ? ids.label : `${ids.label} ${ids.startName}`);
        end?.setAttribute('aria-labelledby', `${ids.label} ${ids.endName}`);

        for (const input of inputs()) {
            if (ids.describedBy) input.setAttribute('aria-describedby', ids.describedBy);
            input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
            if (required) input.setAttribute('aria-required', 'true');
        }

        makeClearOperable();
    }

    const onOpeningGesture = (event) => {
        if (isOpen()) return;
        const index = inputs().indexOf(event.target);
        if (index !== -1) triggerIndex = index;
    };

    // Capture, ahead of antd: by the time a key bubbles back up, antd has already acted on it.
    const onKeyDownCapture = (event) => {
        pointerOutside = false;
        onOpeningGesture(event);

        if (event.key === 'Escape') {
            escapeFoundOpen = isOpen();
            return;
        }

        if (event.key !== 'Tab' || ! isOpen()) return;

        // Out of a popup rendered into another ancestor: on to what follows the host there, not to
        // whatever follows the ancestor's end.
        if (ancestor && ! event.shiftKey && popupRoot.contains(event.target) && event.target === tabbables(popupRoot).at(-1)) {
            const after = tabbables(ancestor).find((element) => ! root.contains(element) && ! popupRoot.contains(element)
                && (root.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING));
            if (after) {
                event.preventDefault();
                event.stopPropagation();
                after.focus();
            }
            return;
        }

        // Into the presets, past the clear control whose focus would close the popup, and past
        // whatever sits between the inputs and a popup rendered into another ancestor. Stopped here,
        // so antd never sees this Tab.
        const [first] = presetButtons();
        const into = ! event.shiftKey && first && event.target === inputs().at(-1);
        const back = event.shiftKey && first && event.target === first;
        if (! into && ! back) return;

        event.preventDefault();
        event.stopPropagation();
        (into ? first : inputs().at(-1))?.focus();
    };

    const onKeyDown = (event) => {
        if (event.key !== 'Escape' || ! escapeFoundOpen) return;
        escapeFoundOpen = false;
        // The picker's Escape, not a dialog's: a surrounding <x-modal> listens on window.
        event.stopPropagation();
        // antd only hears an Escape that starts in its inputs; one from a preset is closed here.
        if (! mount.contains(event.target)) close({ refocus: true });
    };

    // A press on a preset row, seen before antd's: the row's padding is passed to its label (the
    // component records the key only there), and the press is reported to the host.
    const onPopupClickCapture = (event) => {
        const row = event.target?.closest?.(PRESET_ROWS);
        if (! row || ! popupRoot.contains(row)) return;

        const label = labelOf(row);
        if (! label.contains(event.target)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            label.click();
            return;
        }

        onPresetPress(presetOrder()[presetRows().indexOf(row)] ?? null);
    };

    const onDocumentKeyDown = (event) => {
        if (event.key !== 'Escape' || ! isOpen()) return;
        if (event.target !== document.body && event.target !== document.documentElement) return;
        event.stopPropagation();
        close({ refocus: true });
    };

    // Against the picker, not the host: a press on the host's hint or error text is outside it too.
    const onDocumentPointerDown = (event) => {
        pointerOutside = ! mount.contains(event.target) && ! popupRoot.contains(event.target);
    };

    const onFocusOut = (event) => {
        // antd marks an input invalid while it holds text that is not a date, and does not clear the
        // mark when a reader abandons the edit and antd puts the last good date back: once they leave
        // the input, it says what the server says.
        if (mount.contains(event.target)) {
            const want = String(serverInvalid);
            for (const input of inputs()) {
                if (input.getAttribute('aria-invalid') !== want) input.setAttribute('aria-invalid', want);
            }
        }

        const next = event.relatedTarget;
        // null is a click on nothing or the window losing focus: antd's own click-outside handles a click.
        if (! next || root.contains(next) || popupRoot.contains(next)) return;
        if (isOpen()) close({ refocus: false });
    };

    // React builds a preset's onMouseEnter from the mouseout of whatever the pointer left (the input,
    // in the React root at `mount`, since the popup is a portal of that root) or from a mouseover on
    // the preset. Stopped in capture on the host's own nodes, ahead of React's listeners, antd never
    // starts the preview (measured). CSS :hover is untouched.
    const onPresetHoverCapture = (event) => {
        const into = event.type === 'mouseover' ? event.target : event.relatedTarget;
        const presets = into?.closest?.(PRESETS);
        if (! presets) return;

        event.stopPropagation();

        // The same mouseout is how React leaves what the pointer came from (a day cell previewing
        // its date): replayed with a destination just outside the column, the cell is left and no
        // preset is entered.
        const from = event.type === 'mouseout' ? event.target : null;
        if (from && ! from.closest?.(PRESETS)) {
            from.dispatchEvent(new MouseEvent('mouseout', { bubbles: true, relatedTarget: presets.parentElement }));
        }
    };

    const targets = popupRoot === popup ? [root] : [root, popupRoot];
    for (const target of targets) {
        target.addEventListener('mousedown', onOpeningGesture, true);
        target.addEventListener('click', onOpeningGesture, true);
        target.addEventListener('keydown', onKeyDownCapture, true);
        target.addEventListener('keydown', onKeyDown);
        target.addEventListener('focusout', onFocusOut);
        target.addEventListener('mouseover', onPresetHoverCapture, true);
        target.addEventListener('mouseout', onPresetHoverCapture, true);
    }
    popupRoot.addEventListener('click', onPopupClickCapture, true);
    document.addEventListener('keydown', onDocumentKeyDown);
    document.addEventListener('pointerdown', onDocumentPointerDown, true);

    const observer = new MutationObserver(() => {
        if (! warnedPrefix && ! popupRoot.querySelector(DROPDOWN) && popupRoot.querySelector(ANY_PREFIX_DROPDOWN)) {
            warnedPrefix = true;
            console.error('Date filter popup is not antd\'s ant-picker-dropdown: the keyboard layer cannot find it', { id: ids.host });
        }
        makePresetsOperable();
        makeClearOperable();
    });
    observer.observe(popupRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    observer.observe(mount, { childList: true, subtree: true });

    // antd sets aria-invalid on its inputs from its own state on every render; while the server
    // holds a validation error for the bound property, that error stands.
    const invalidGuard = new MutationObserver((records) => {
        if (! serverInvalid) return;
        for (const record of records) {
            if (record.target.getAttribute('aria-invalid') !== 'true') record.target.setAttribute('aria-invalid', 'true');
        }
    });
    invalidGuard.observe(mount, { subtree: true, attributes: true, attributeFilter: ['aria-invalid'] });

    return {
        popupContainer: () => popupRoot,
        wire,
        returnFocus,
        focusTrigger: () => triggerInput()?.focus(),
        /** Whether focus is in the filter: its inputs and clear control, or its popup. */
        hasFocus: () => {
            const active = document.activeElement;
            return Boolean(active) && (root.contains(active) || popupRoot.contains(active));
        },
        destroy() {
            observer.disconnect();
            invalidGuard.disconnect();
            for (const target of targets) {
                target.removeEventListener('mousedown', onOpeningGesture, true);
                target.removeEventListener('click', onOpeningGesture, true);
                target.removeEventListener('keydown', onKeyDownCapture, true);
                target.removeEventListener('keydown', onKeyDown);
                target.removeEventListener('focusout', onFocusOut);
                target.removeEventListener('mouseover', onPresetHoverCapture, true);
                target.removeEventListener('mouseout', onPresetHoverCapture, true);
            }
            popupRoot.removeEventListener('click', onPopupClickCapture, true);
            document.removeEventListener('keydown', onDocumentKeyDown);
            document.removeEventListener('pointerdown', onDocumentPointerDown, true);
            if (popupRoot !== popup) popupRoot.remove();
        },
    };
}
